/* fbt-fitment.js — fitment fetch + resolution helpers for the FBT section.
 * Exposed as window.FBTFitment (browser) and module.exports (node tests).
 * Pure functions have no DOM/network deps; network functions accept an
 * injectable fetchImpl for testing. */
(function (root) {
  'use strict';

  function firstOf(value) {
    if (value == null) return null;
    if (Array.isArray(value)) return value.length ? value[0] : null;
    if (typeof value === 'string') {
      const t = value.trim();
      if (t.charAt(0) === '[') {
        try { const a = JSON.parse(t); return Array.isArray(a) && a.length ? a[0] : null; } catch (_) {}
      }
      return value;
    }
    return value;
  }

  const SF_ENDPOINT = '/api/2023-10/graphql.json';

  function pickFetch(fetchImpl) {
    if (fetchImpl) return fetchImpl;
    if (typeof fetch !== 'undefined') return fetch;
    return null;
  }

  async function fetchFitmentCompatibility(gids, sfToken, fetchImpl) {
    const f = pickFetch(fetchImpl);
    const out = new Map();
    if (!gids || !gids.length || !f) return out;
    const query = 'query($ids:[ID!]!){ nodes(ids:$ids){ ... on Product { id ' +
      'm: metafield(namespace:"custom", key:"fitment_compatibility"){ value } ' +
      's: metafield(namespace:"custom", key:"submodel"){ value } } } }';
    const res = await f(SF_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': sfToken },
      body: JSON.stringify({ query: query, variables: { ids: gids } })
    });
    if (res && res.ok === false) throw new Error('wool SF API ' + res.status);
    const json = await res.json();
    const nodes = (json && json.data && json.data.nodes) || [];
    nodes.filter(Boolean).forEach(function (n) {
      out.set(n.id, {
        rules: parseFitmentCompatibility(n.m && n.m.value),
        submodels: parseStringList(n.s && n.s.value)
      });
    });
    return out;
  }

  // Parse a list.single_line_text_field metafield value (a JSON-array string)
  // into a plain string array; tolerant of nulls/malformed input.
  function parseStringList(value) {
    if (value == null) return [];
    let data = value;
    if (typeof value === 'string') {
      try { data = JSON.parse(value); } catch (_) { return []; }
    }
    return Array.isArray(data) ? data.filter(function (x) { return x != null; }).map(String) : [];
  }

  async function fetchQuestionCatalog(types, sfToken, fetchImpl) {
    const f = pickFetch(fetchImpl);
    const out = new Map();
    if (!types || !types.length || !f) return out;
    // One aliased query branch per type. Type handles are simple identifiers (body_type, ...).
    const branches = types.map(function (t, i) {
      return 'q' + i + ': metaobjects(type:"' + t + '", first:50){ nodes{ type fields{ key value } } }';
    }).join(' ');
    const res = await f(SF_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': sfToken },
      body: JSON.stringify({ query: '{ ' + branches + ' }' })
    });
    if (res && res.ok === false) throw new Error('wool SF API ' + res.status);
    const json = await res.json();
    const data = (json && json.data) || {};
    types.forEach(function (t, i) {
      const nodes = (data['q' + i] && data['q' + i].nodes) || [];
      const options = new Map();
      let labelEn = '', labelEs = '';
      nodes.forEach(function (node) {
        const fields = {};
        (node.fields || []).forEach(function (fl) { if (fl && fl.key != null) fields[fl.key] = fl.value; });
        const display = firstOf(fields.option_1) || firstOf(fields.display_name) || '';
        if (!labelEn && fields.question) labelEn = firstOf(fields.question) || '';
        if (!labelEs && fields.spanish_question) labelEs = firstOf(fields.spanish_question) || '';
        if (display) {
          options.set(display, {
            display: display,
            sub_label: fields.sub_label || '',
            spanish_sub_label: fields.spanish_sub_label || '',
            modal_content: fields.modal_content || '',
            label: fields.label || ''
          });
        }
      });
      out.set(t, { label_en: labelEn, label_es: labelEs, options: options });
    });
    return out;
  }

  function parseFitmentCompatibility(value) {
    if (value == null) return [];
    let data = value;
    if (typeof value === 'string') {
      try { data = JSON.parse(value); } catch (_) { return []; }
    }
    return Array.isArray(data) ? data.filter(function (r) { return r && r.question; }) : [];
  }

  function _norm(v) { return String(v == null ? '' : v).trim().toLowerCase(); }

  function _condMatches(list, value, numeric) {
    if (!Array.isArray(list) || list.length === 0) return true; // empty/missing = any
    if (numeric) {
      const n = Number(value);
      return list.some(function (x) { return Number(x) === n; });
    }
    const nv = _norm(value);
    return list.some(function (x) { return _norm(x) === nv; });
  }

  function ruleAppliesToVehicle(rule, vehicle) {
    if (!rule || !vehicle) return false;
    const c = rule.conditions || {};
    // The YMM garage captures year and submodel but never the engine, so a blank
    // vehicle engine must not exclude an otherwise-matching rule — otherwise every
    // engine-gated product becomes permanently invisible (an unknown attribute is
    // never a basis for exclusion). A known engine still filters normally.
    const engineOk = _norm(vehicle.engine) === ''
      ? true
      : _condMatches(c.engines, vehicle.engine, false);
    return _condMatches(c.years, vehicle.year, true) &&
           _condMatches(c.submodels, vehicle.submodel, false) &&
           engineOk;
  }

  function _applicableRules(rules, vehicle) {
    return (rules || []).filter(function (r) { return ruleAppliesToVehicle(r, vehicle); });
  }

  function productIsViable(rules, vehicle) {
    return _applicableRules(rules, vehicle).some(function (r) { return r.mode === 'compatible'; });
  }

  function productQuestionKeys(rules, vehicle) {
    const seen = [];
    _applicableRules(rules, vehicle).forEach(function (r) {
      if (seen.indexOf(r.question) === -1) seen.push(r.question);
    });
    return seen;
  }

  function productAnswerOptions(rules, vehicle, questionKey) {
    const seen = [];
    _applicableRules(rules, vehicle).forEach(function (r) {
      if (r.question === questionKey && seen.indexOf(r.answer) === -1) seen.push(r.answer);
    });
    return seen;
  }

  function evaluateProduct(rules, vehicle, answers) {
    answers = answers || {};
    const applicable = _applicableRules(rules, vehicle);
    const keys = [];
    applicable.forEach(function (r) { if (keys.indexOf(r.question) === -1) keys.push(r.question); });

    const pendingKeys = [];
    let failed = false;

    keys.forEach(function (q) {
      const ans = answers[q];
      if (ans == null || ans === '') { pendingKeys.push(q); return; }
      const matches = applicable.filter(function (r) { return r.question === q && _norm(r.answer) === _norm(ans); });
      if (matches.length === 0) { failed = true; return; }            // decision (a): no rule = fail
      if (matches.some(function (r) { return r.mode === 'incompatible'; })) { failed = true; }
    });

    if (failed) return { state: 'nofit', pendingKeys: pendingKeys };
    if (pendingKeys.length) return { state: 'pending', pendingKeys: pendingKeys };
    return { state: 'fit', pendingKeys: [] };
  }

  // True when at least one rule constrains the vehicle's submodel, i.e. the
  // product's fitment cannot be evaluated without knowing the submodel.
  function productSubmodelGated(rules) {
    return (rules || []).some(function (r) {
      return r && r.conditions && Array.isArray(r.conditions.submodels) && r.conditions.submodels.length > 0;
    });
  }

  // Trim gate independent of fitment_compatibility: a product is allowed when
  // it declares no submodel list, when the vehicle's submodel is unknown (it is
  // asked separately), or when the vehicle's submodel is in the list.
  function submodelListAllows(list, submodel) {
    if (!Array.isArray(list) || list.length === 0) return true;
    const sm = _norm(submodel);
    if (!sm) return true;
    return list.some(function (s) { return _norm(s) === sm; });
  }

  // De-duplicated union of every submodel named across a product's rules.
  function productSubmodels(rules) {
    const seen = [];
    (rules || []).forEach(function (r) {
      const list = r && r.conditions && r.conditions.submodels;
      if (Array.isArray(list)) list.forEach(function (s) {
        if (s != null && seen.indexOf(s) === -1) seen.push(s);
      });
    });
    return seen;
  }

  const api = {
    firstOf: firstOf,
    parseFitmentCompatibility: parseFitmentCompatibility,
    ruleAppliesToVehicle: ruleAppliesToVehicle,
    productIsViable: productIsViable,
    productQuestionKeys: productQuestionKeys,
    productAnswerOptions: productAnswerOptions,
    evaluateProduct: evaluateProduct,
    productSubmodelGated: productSubmodelGated,
    productSubmodels: productSubmodels,
    submodelListAllows: submodelListAllows,
    fetchFitmentCompatibility: fetchFitmentCompatibility,
    fetchQuestionCatalog: fetchQuestionCatalog
  };

  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (root) root.FBTFitment = api;
})(typeof window !== 'undefined' ? window : null);
